import type { Node, Edge } from '@xyflow/react';
import type { HandleActionType, WidgetNodeData } from './types';
import { widgetOutputs, widgetRegistry } from './registry';
import { actionLabels } from './action-labels';
import { isOfficeClosedConfigured, isStartOutputSatisfied, isZeroRouteConfigured } from './handle-action-rules';
import { startActionOutputs } from '@legacy-shared/flow/start-output';

export type FlowIssue = {
  level: 'error' | 'warning';
  nodeId?: string;
  message: string;
};

export type FlowValidationResult = {
  valid: boolean;
  issues: FlowIssue[];
};

const OPTIONAL_HANDLES = new Set(['completed']);

type StartOutputRequirement = {
  isConfigured: (startNode: Node<WidgetNodeData>) => boolean;
  unconfiguredMessage: string;
  handleId: string;
};

const startOutputRequirements: Record<Exclude<HandleActionType, 'hangup'>, StartOutputRequirement> = {
  officeClosed: {
    isConfigured: isOfficeClosedConfigured,
    unconfiguredMessage: 'requires a business hours schedule',
    handleId: startActionOutputs.officeClosed,
  },
  zero: {
    isConfigured: isZeroRouteConfigured,
    unconfiguredMessage: 'requires zero pressed to be enabled',
    handleId: startActionOutputs.zero,
  },
};

function startOutput(startNode: Node<WidgetNodeData>, handleId: string) {
  return widgetOutputs('start', startNode.data.config as Record<string, unknown>)?.find((o) => o.id === handleId);
}

function unmetActionRequirement(startNode: Node<WidgetNodeData>, edges: Edge[], type: HandleActionType) {
  if (type === 'hangup') return undefined;

  const requirement = startOutputRequirements[type];
  if (!requirement.isConfigured(startNode)) return requirement.unconfiguredMessage;

  const output = startOutput(startNode, requirement.handleId);
  if (!output || isStartOutputSatisfied(startNode, edges, output.id)) return undefined;

  return `requires the "${output.label}" output to be connected or given a "${actionLabels.hangup}" action`;
}

export function validateFlow(nodes: Node<WidgetNodeData>[], edges: Edge[]): FlowValidationResult {
  const issues: FlowIssue[] = [];

  if (nodes.length === 0) {
    issues.push({ level: 'error', message: 'Flow is empty' });
    return { valid: false, issues };
  }

  const startNodes = nodes.filter((n) => n.type === 'start');
  if (startNodes.length === 0) {
    issues.push({ level: 'error', message: 'Flow must have a Start node' });
  } else if (startNodes.length > 1) {
    issues.push({ level: 'error', message: 'Flow must have exactly one Start node' });
  }

  for (const node of nodes) {
    if (!node.type) continue;
    const widget = widgetRegistry.get(node.type);
    if (!widget) continue;

    const result = widget.validation.validateConfig(node.data.config as Record<string, unknown>);
    for (const error of result.errors) {
      issues.push({ level: 'error', nodeId: node.id, message: `${widget.name}: ${error}` });
    }
    for (const warning of result.warnings ?? []) {
      issues.push({ level: 'warning', nodeId: node.id, message: `${widget.name}: ${warning}` });
    }
  }

  const startNode = startNodes[0];
  if (!startNode) {
    return { valid: issues.every((i) => i.level !== 'error'), issues };
  }

  const reachable = new Set<string>();
  const queue = [startNode.id];
  const adjacency = new Map<string, string[]>();
  for (const edge of edges) {
    const targets = adjacency.get(edge.source) ?? [];
    targets.push(edge.target);
    adjacency.set(edge.source, targets);
  }

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (reachable.has(current)) continue;
    reachable.add(current);
    for (const neighbor of adjacency.get(current) ?? []) {
      if (!reachable.has(neighbor)) queue.push(neighbor);
    }
  }

  for (const node of nodes) {
    if (node.type === 'start') continue;
    if (!reachable.has(node.id)) {
      const widget = node.type ? widgetRegistry.get(node.type) : undefined;
      const name = widget?.name ?? node.type ?? 'Unknown';
      issues.push({ level: 'warning', nodeId: node.id, message: `${name} is unreachable from Start` });
    }
  }

  const edgesBySource = new Map<string, Set<string>>();
  for (const edge of edges) {
    const key = edge.source;
    const handles = edgesBySource.get(key) ?? new Set();
    handles.add(edge.sourceHandle ?? 'default');
    edgesBySource.set(key, handles);
  }

  for (const node of nodes) {
    if (!node.type || !reachable.has(node.id)) continue;

    const outputs = widgetOutputs(node.type, node.data.config as Record<string, unknown>);
    if (!outputs || outputs.length === 0) continue;

    const connectedHandles = edgesBySource.get(node.id) ?? new Set();
    const nodeHandleActions = node.data.handleActions ?? {};
    const widget = widgetRegistry.get(node.type);
    const name = widget?.name ?? node.type;

    for (const output of outputs) {
      if (connectedHandles.has(output.id)) continue;

      const action = nodeHandleActions[output.id];
      if (action) {
        const requirement = unmetActionRequirement(startNode, edges, action.type);
        if (requirement) {
          issues.push({
            level: 'warning',
            nodeId: node.id,
            message: `${name}: "${output.label}" action "${actionLabels[action.type]}" ${requirement}`,
          });
        }
        continue;
      }
      if (OPTIONAL_HANDLES.has(output.id)) continue;

      issues.push({
        level: 'error',
        nodeId: node.id,
        message: `${name}: "${output.label}" output is not connected`,
      });
    }
  }

  return { valid: issues.every((i) => i.level !== 'error'), issues };
}
